/**
 * Acknowledges an OncoEMR task with one automatic retry on version conflict (409).
 * @module ackOncoemrTaskWithStaleRetry
 */

import { ackOncoemrTask } from "../../../../../api/postCall/ackOncoemrTask";
import { isOncoemrTaskAckStaleError } from "../../../../../api/postCall/oncoemrTaskAckErrors";
import type { AckOncoemrTaskResponse } from "../../../../../data-model/oncoemrTasksModel";

/**
 * Posts task ack and retries once with the server version when the row changed (409).
 *
 * @param taskRowId - `tasks.id` for the row to acknowledge.
 * @param orgId - Organization scope for the ack request.
 * @param expectedVersion - Optimistic concurrency version from the list/inbox row.
 * @param signal - Optional abort signal for in-flight cancellation.
 * @returns Ack response with cleared `is_updated` and the new row version.
 * @throws Error When ack fails after retry or the task is missing (404).
 */
export const ackOncoemrTaskWithStaleRetry = async (
  taskRowId: string,
  orgId: string,
  expectedVersion: number,
  signal?: AbortSignal,
): Promise<AckOncoemrTaskResponse> => {
  try {
    return await ackOncoemrTask(
      taskRowId,
      { org_id: orgId, expected_version: expectedVersion },
      signal,
    );
  } catch (error) {
    if (
      isOncoemrTaskAckStaleError(error) &&
      Number.isFinite(error.currentVersion)
    ) {
      return await ackOncoemrTask(
        taskRowId,
        { org_id: orgId, expected_version: error.currentVersion },
        signal,
      );
    }
    throw error;
  }
};
